import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPurchaseOrderById } from "../../services/api/boq/purchaseService";
import {
  getVendorById,
  getVendors,
} from "../../services/api/boq/VendorService";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import { fetchBoqByProject } from "../../services/api/boq/boqService";
import toast from "react-hot-toast";
import { ArrowLeft, CheckCircle, X, Clock } from "lucide-react";

export default function PurchaseOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorName, setVendorName] = useState("Loading...");

  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [boqs, setBoqs] = useState([]);

  // useEffect(() => {
  const loadPO = async () => {
    try {
      const res = await getPurchaseOrderById(id);
      setPo(res.data);

      const vId = res.data?.vendor_id;
      if (vId) {
        fetchVendorName(vId);
      }

      if (res.data.data.project_id) {
        const boqRes = await fetchBoqByProject(res.data.data.project_id);

        if (boqRes?.boq) {
          setBoqs([boqRes.boq]);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const fetchVendorName = async (vendorId) => {
    try {
      const res = await getVendorById(vendorId);
      if (res.data?.data?.name) {
        setVendorName(res.data.data.name);
      } else {
        console.warn("Vendor object found, but 'name' field is missing");
        setVendorName("Unknown Vendor");
      }
    } catch (err) {
      console.error("Failed to fetch vendor name:", err);
      setVendorName("Name not found");
    }
  };

  //   loadPO();
  //   loadVendors();
  //   loadProjects();
  // }, [id]);
  useEffect(() => {
    loadPO(); // Called on mount
    loadVendors();
    loadProjects();
  }, [id]);
  const loadVendors = async () => {
    const res = await getVendors();
    setVendors(res?.data?.data?.data || []);
  };

  const loadProjects = async () => {
    const res = await fetchProjectsApi();
    setProjects(res?.data?.data?.data || []);
  };

  // 3. Add the handlePOAction function here
  const handlePOAction = async (type) => {
    const confirmAction = window.confirm(
      `Are you sure you want to ${type} this Purchase Order?`,
    );
    if (!confirmAction) return;

    const toastId = toast.loading(`Processing ${type}...`);

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/purchase-orders/${id}/${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        toast.success(
          `PO ${type === "approve" ? "Approved" : "Rejected"} successfully`,
          { id: toastId },
        );
        // 4. Refresh page data using the shared loadPO function
        await loadPO();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || `Failed to ${type} PO`, {
          id: toastId,
        });
      }
    } catch (error) {
      console.error(`Error during PO ${type}:`, error);
      toast.error("Network error occurred", { id: toastId });
    }
  };

  const vendorMap = Object.fromEntries(vendors.map((v) => [v.id, v.name]));

  const projectMap = Object.fromEntries(
    projects.map((p) => [p.id, p.project_name]),
  );

  const boqMap = Object.fromEntries(boqs.map((b) => [b.id, b.boq_name]));

  if (loading) return <div className="p-4">Loading Purchase Order...</div>;
  if (!po) return <div className="p-4">Purchase Order not found</div>;

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white p-4 rounded-xl shadow-md flex flex-col md:flex-row md:justify-between md:items-center">
        <div>
          <h2 className="text-2xl font-bold">{po.po_number}</h2>
          <p className="text-gray-500 text-sm mt-1">
            Created on {new Date(po.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center mt-3 md:mt-0 gap-4">
          <span
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              po.status === "pending"
                ? "bg-yellow-100 text-yellow-800"
                : po.status === "approved"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
            }`}
          >
            {po.status.toUpperCase()}
          </span>
          <span className="text-lg font-semibold">₹{po.total_amount}</span>
        </div>
      </div>

      {/* PO Info Section */}
      <div className="bg-white p-4 rounded-xl shadow-md grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-gray-600">
            <strong>Vendor:</strong> {vendorName}
            {po.vendor_id && ` (ID: ${po.vendor_id})`}
          </p>

          <p className="text-gray-600 mt-1">
            <strong>Project:</strong> {projectMap[po.project_id] || "-"}
            {po.project_id && ` (ID: ${po.project_id})`}
          </p>
          <p className="text-gray-600 mt-1">
            <strong>Deliver To:</strong> {po.deliver_to || "-"}
          </p>
        </div>

        <div>
          <p className="text-gray-600">
            <strong>Order Date:</strong> {po.order_date}
          </p>
          <p className="text-gray-600 mt-1">
            <strong>Created At:</strong>{" "}
            {new Date(po.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Items Section */}
      <div>
        <h3 className="text-xl font-semibold mb-3">Items</h3>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <table className="w-full text-sm border">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-2 border text-left">SN #</th>
                <th className="p-2 border text-left">Item Name</th>
                <th className="p-2 border text-left">BOQ Item ID</th>
                <th className="p-2 border text-left">Quantity</th>
                <th className="p-2 border text-left">Unit Price</th>
                <th className="p-2 border text-left">Total</th>
              </tr>
            </thead>
            <tbody>
              {po.items.map((item, index) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="p-2 border text-left">{index + 1}</td>
                  <td className="p-2 border text-left">{item.item_name}</td>
                  <td className="p-2 border text-left">
                    {item.boq_item_id || "N/A"}
                  </td>
                  <td className="p-2 border text-left">{item.ordered_qty}</td>
                  <td className="p-2 border text-left">₹{item.unit_price}</td>
                  <td className="p-2 border text-left">₹{item.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {po.items.map((item) => (
            <div key={item.id} className="bg-white shadow-md rounded-xl p-4">
              <p>
                <strong>Item:</strong> {item.item_name}
              </p>
              <p>
                <strong>BOQ ID:</strong>{" "}
                {boqMap[item.boq_id] || item.boq_id || "N/A"}
              </p>
              <p>
                <strong>Quantity:</strong> {item.ordered_qty}
              </p>
              <p>
                <strong>Unit Price:</strong> ₹{item.unit_price}
              </p>
              <p>
                <strong>Total:</strong> ₹{item.total}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t bg-white p-4 rounded-xl shadow-md">
        <p className="text-gray-600 mt-4">
          <strong>Notes:</strong> {po.notes || "No additional notes"}
        </p>
        <p className="text-gray-600 mt-1">
          <strong>Terms and Conditions:</strong>{" "}
          {po.t_c || "No terms and conditions specified"}
        </p>
        <p className="text-gray-600 mt-1">
          <strong>Total Amount:</strong> {po.total_amount || 0}
        </p>
        <p className="text-gray-600 mt-1">
          <strong>GST Amount:</strong> {po.gst_amount || 0}
        </p>
        <p className="text-gray-600 mt-1">
          <strong>Note:</strong> All amounts are in INR
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-start flex-wrap gap-6">
        <button
          onClick={() => navigate("/purchase-orders")}
          // className="px-4 py-2 bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition"
          className="flex items-center justify-center gap-2 px-5 py-1.5 min-w-[70px] bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-all shadow-sm"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        <button
          onClick={() => navigate(`/po/${po.id}/payments`)}
          //className="px-4 py-2 bg-green-200 text-white-600 hover:text-green-800 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-200 transition"
          className="flex items-center justify-center gap-2 px-5 py-1.5 min-w-[50px] bg-blue-200 text-black-600 rounded-lg hover:bg-blue-100 font-semibold transition-all border border-blue-100"
        >
          <Clock size={18} />
          <span>History</span>
        </button>
        {/* Optional: Add Export buttons here */}
        {/* NEW: Dynamic Approve/Reject buttons logic based on your requirement */}
        {po.status === "pending" && (
          <div className="flex flex-wrap gap-6">
            <button
              onClick={() => handlePOAction("approve")}
              //className="px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 flex items-center gap-2.5 text-sm font-bold transition shadow-sm"
              className="flex items-center justify-center gap-2 px-5 py-1.5 min-w-[50px] bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold transition-all shadow-md active:scale-95"
            >
              <CheckCircle size={18} />
              <span>Approve</span>
            </button>
            <button
              onClick={() => handlePOAction("reject")}
              //className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 flex items-center gap-2.5 text-sm font-bold transition shadow-sm"
              className="flex items-center justify-center gap-2 px-5 py-1.5 min-w-[50px] bg-red-200 text-red-600 rounded-lg hover:bg-red-100 font-bold transition-all border border-red-100 shadow-sm active:scale-95"
            >
              <X size={18} />
              <span>Reject</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
